import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { MENU_ALIAS_PREFIX, MENU_ALIAS_PRESETS, stripMenuAlias } from '@shared/config/menuAlias';
import { useMenuStore } from '@shared/store/menuStore';
import { buildMenuParentMap, extractRootMenuKeys } from '@shared/utils/menuNormalization';
import type { AntdMenuItem } from '@shared/types/menu';

type MenuClickEvent = Parameters<NonNullable<MenuProps['onClick']>>[0];

/**
 * Хук для управления навигацией в сайдбаре
 */
export function useSidebarNavigation(menuItems: AntdMenuItem[], isMobile: boolean) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openKeys, setOpenKeys } = useMenuStore();
  const prevPathnameRef = useRef(location.pathname);

  // Построить карту родительских ключей и корневых ключей
  const parentKeyMap = useMemo(() => buildMenuParentMap(menuItems), [menuItems]);
  const rootMenuKeys = useMemo(() => extractRootMenuKeys(menuItems), [menuItems]);

  // Найти корневой ключ меню для заданного ключа
  const findRootMenuKey = useCallback(
    (key: string): string | undefined => {
      if (!key) {
        return undefined;
      }

      let current = key;
      const visited = new Set<string>();

      while (parentKeyMap.has(current) && !visited.has(current)) {
        visited.add(current);
        current = parentKeyMap.get(current)!;
      }

      if (rootMenuKeys.includes(current)) {
        return current;
      }

      return rootMenuKeys.includes(key) ? key : undefined;
    },
    [parentKeyMap, rootMenuKeys],
  );

  // Нормализовать открытые ключи (accordion - только один открытый)
  const normalizeMenuOpenKeys = useCallback(
    (keys: string[]): string[] => {
      if (!keys || keys.length === 0) {
        return [];
      }

      const latestKey = keys[keys.length - 1];
      const activeRoot = findRootMenuKey(latestKey) ?? latestKey;
      const normalized: string[] = [];

      keys.forEach((key) => {
        const root = findRootMenuKey(key);
        if (root && root !== activeRoot) {
          return;
        }

        if (root === activeRoot) {
          if (!normalized.includes(root)) {
            normalized.push(root);
          }
          if (!normalized.includes(key)) {
            normalized.push(key);
          }
        } else if (!root && key === activeRoot) {
          if (!normalized.includes(key)) {
            normalized.push(key);
          }
        }
      });

      if (!normalized.includes(activeRoot)) {
        normalized.unshift(activeRoot);
      }

      return normalized;
    },
    [findRootMenuKey],
  );

  // Обработчик клика по пункту меню
  const handleMenuClick: MenuProps['onClick'] = useCallback(
    (e: MenuClickEvent) => {
      const key = e.key;

      // Внешние ссылки
      if (key.startsWith('external::')) {
        const targetUrl = key.replace('external::', '');
        if (targetUrl) {
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
        }
        if (isMobile) {
          // Закрытие мобильного меню обрабатывается в родительском компоненте
        }
        return;
      }

      // Swagger
      if (key === 'open-swagger') {
        const swaggerUrl = (() => {
          if (import.meta.env.MODE === 'development') {
            const devUrl = new URL(window.location.origin);
            devUrl.port = '8081';
            devUrl.pathname = '/swagger/index.html';
            devUrl.search = '';
            devUrl.hash = '';
            return devUrl.toString();
          }

          const prodUrl = new URL(window.location.origin);
          prodUrl.pathname = '/swagger/index.html';
          prodUrl.search = '';
          prodUrl.hash = '';
          return prodUrl.toString();
        })();

        window.open(swaggerUrl, '_blank', 'noopener,noreferrer');
        if (isMobile) {
          // Закрытие мобильного меню обрабатывается в родительском компоненте
        }
        return;
      }

      // Псевдоним: второй пункт меню, ведущий на УЖЕ занятый другим пунктом путь.
      // antd требует уникальный key, но связка «key = адрес навигации» наша, не
      // библиотечная — ровно поэтому рядом уже живут `external::` и
      // `open-swagger`. Псевдоним снимает требование уникальности, не засоряя
      // адресную строку служебным query.
      if (key.startsWith(MENU_ALIAS_PREFIX)) {
        const target = stripMenuAlias(key);
        if (target.startsWith('/')) {
          // Предустановка экрана едет navigation state'ом, а не query-параметром:
          // адрес остаётся чистым (ровно поэтому псевдоним и заведён вместо
          // `?from=`), второй роут под тот же экран не нужен.
          //
          // Псевдоним без предустановки навигируется РОВНО как раньше, одним
          // аргументом: пустой state — это лишнее состояние в истории браузера.
          const preset = MENU_ALIAS_PRESETS[target];
          if (preset) {
            navigate(target, { state: preset });
          } else {
            navigate(target);
          }
        }
        return;
      }

      // Внутренняя навигация
      if (key.startsWith('/')) {
        navigate(key);
      }
    },
    [navigate, isMobile],
  );

  // Все ключи-пути меню — чтобы отличить пункт с query от пункта без него.
  //
  // Ключи-псевдонимы (`alias::`) сюда НЕ попадают намеренно. Псевдоним и
  // канонический пункт ведут на один и тот же адрес, поэтому по URL различить
  // их невозможно в принципе — подсветку владеет канонический пункт, а
  // псевдоним не подсвечивается никогда. Это честнее, чем подсвечивать оба
  // (antd всё равно нарисует один) или тащить в адрес служебный параметр
  // только ради различения.
  const menuPathKeys = useMemo(() => {
    const keys: string[] = [];
    const walk = (items: AntdMenuItem[]) => {
      items.forEach((item) => {
        if (!item) {
          return;
        }
        const key = String((item as { key?: unknown }).key ?? '');
        if (key.startsWith('/')) {
          keys.push(key);
        }
        const children = (item as { children?: AntdMenuItem[] }).children;
        if (children) {
          walk(children);
        }
      });
    };
    walk(menuItems);
    return keys;
  }, [menuItems]);

  // Определить активный пункт меню на основе URL
  const selectedKeys = useMemo(() => {
    const transportDetail = /^\/transport\/([1-9]\d*)$/.exec(location.pathname);
    if (transportDetail && Number(transportDetail[1]) <= 2_147_483_647) {
      return ['/transport'];
    }

    // Пункты-пресеты («Бывшие», «Календарь») отличаются от базового списка
    // только query, поэтому подсветка обязана его учитывать: иначе на
    // `/customers?preset=ex` подсветится «Список абонентов», и оператор не
    // видит, какой пункт открыт.
    //
    // Сравнение — по вхождению, а не по равенству строк: страница дописывает в
    // адрес свои параметры (`/tasks?page=1&limit=20&view=calendar`), и точное
    // совпадение с ключом `/tasks?view=calendar` не наступает никогда.
    // Из подходящих берём самый специфичный, иначе безусловно выигрывал бы
    // ключ без параметров.
    const current = new URLSearchParams(location.search);
    let bestKey: string | null = null;
    let bestScore = -1;

    menuPathKeys.forEach((key) => {
      const [keyPath, keyQuery = ''] = key.split('?');
      if (keyPath !== location.pathname) {
        return;
      }

      const expected = [...new URLSearchParams(keyQuery).entries()];
      if (!expected.every(([name, value]) => current.get(name) === value)) {
        return;
      }

      if (expected.length > bestScore) {
        bestKey = key;
        bestScore = expected.length;
      }
    });

    // Вход-псевдоним владеет подсветкой, ПОКА действует его предустановка.
    // Адрес у обоих входов один, различить их по URL нельзя — но состояние
    // навигации (`state`) несёт ровно то, чем второй вход отличается от первого,
    // и подсветка обязана следовать за ним. Иначе оператор жмёт «Активность в
    // интернете», попадает на экран с метрикой биллинга, а в меню подсвечен
    // соседний пункт «Активность в сети» — экран говорит одно, меню другое.
    // При прямом заходе и после перезагрузки state пуст, экран открывается в
    // состоянии по умолчанию — и подсветка возвращается каноническому пункту.
    const preset = MENU_ALIAS_PRESETS[location.pathname];
    const state = (location.state ?? null) as Record<string, unknown> | null;
    if (preset && state && Object.entries(preset).every(([name, value]) => state[name] === value)) {
      return [`${MENU_ALIAS_PREFIX}${location.pathname}`];
    }

    return bestKey ? [bestKey] : [location.pathname];
  }, [location.pathname, location.search, location.state, menuPathKeys]);

  // Автоматически открывать нужную группу при изменении URL
  useEffect(() => {
    const path = location.pathname;

    // Проверяем: URL реально изменился?
    if (prevPathnameRef.current === path) {
      return; // URL не изменился - ничего не делаем
    }

    prevPathnameRef.current = path;

    // Определяем группу по пути
    let groupKey: string | null = null;

    if (path.startsWith('/customers')) groupKey = 'customers';
    else if (path.startsWith('/tariffs')) groupKey = 'tariffs';
    else if (path.startsWith('/messages')) groupKey = 'messages';
    else if (path.startsWith('/accounting')) groupKey = 'accounting';
    else if (path.startsWith('/support')) groupKey = 'support';
    else if (path.startsWith('/tasks')) groupKey = 'tasks';
    else if (path.startsWith('/nodes')) groupKey = 'nodes';
    else if (path.startsWith('/cable')) groupKey = 'cable';
    else if (path.startsWith('/devices') || path.startsWith('/equipment/devices')) groupKey = 'devices';
    else if (path.startsWith('/warehouse')) groupKey = 'warehouse';
    else if (path.startsWith('/employees')) groupKey = 'employees';
    else if (path.startsWith('/transport')) groupKey = 'transport';
    else if (path.startsWith('/users')) groupKey = 'users';
    else if (path.startsWith('/settings')) groupKey = 'settings';
    else if (path.startsWith('/advertisement')) groupKey = 'advertisement';
    else if (path.startsWith('/notepad')) groupKey = 'notepad';
    else if (path.startsWith('/statistics')) groupKey = 'statistics';
    else if (path.startsWith('/map')) groupKey = 'map';

    // Открываем нужную группу ТОЛЬКО при изменении URL
    if (groupKey) {
      setOpenKeys([groupKey]);
    } else if (path === '/dashboard') {
      setOpenKeys([]);
    }
  }, [location.pathname, setOpenKeys]);

  // При загрузке проверяем что openKeys содержит максимум 1 элемент (accordion)
  useEffect(() => {
    if (openKeys && openKeys.length > 1) {
      // Исправляем множественное раскрытие из localStorage
      setOpenKeys([openKeys[0]]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    selectedKeys,
    openKeys: openKeys || [],
    setOpenKeys: (keys: string[]) => setOpenKeys(normalizeMenuOpenKeys(keys)),
    handleMenuClick,
  };
}
